import React, { useEffect, useMemo } from 'react';

import { Controller, useForm } from 'react-hook-form';
import dayjs from '~/timeInit';

import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import { useUpdateClaimCode } from '~/hooks/apis/claimCampaigns';

import type { ClaimCode } from '~/services/LuxLoyalty';

interface EditClaimCodeDialogProps {
  open: boolean;
  campaignId: string;
  code: ClaimCode | null;
  isSharedCode: boolean;
  onClose: () => void;
}

interface FormData {
  code: string;
  expiresAt: string;
  maxRedemptions: string;
}

function toLocalDatetime(iso: string | null | undefined): string {
  if (!iso) return '';
  return dayjs(iso).format('YYYY-MM-DDTHH:mm');
}

export default function EditClaimCodeDialog({
  open,
  campaignId,
  code,
  isSharedCode,
  onClose,
}: EditClaimCodeDialogProps) {
  const updateMutation = useUpdateClaimCode();

  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: { code: '', expiresAt: '', maxRedemptions: '' },
  });

  useEffect(() => {
    if (open && code) {
      reset({
        code: code.code,
        expiresAt: toLocalDatetime(code.expiresAt),
        maxRedemptions: code.maxRedemptions != null ? String(code.maxRedemptions) : '',
      });
    }
  }, [open, code, reset]);

  const codeFieldDisabled = useMemo(() => {
    if (!code) return true;
    if (isSharedCode) return code.currentRedemptions > 0;
    return code.status === 'CLAIMED';
  }, [code, isSharedCode]);

  const onSubmit = handleSubmit(async (data) => {
    if (!code) return;

    const body: Record<string, unknown> = {};

    if (data.code !== code.code) {
      body.code = data.code;
    }

    const newExpires = data.expiresAt || null;
    const oldExpires = code.expiresAt ? toLocalDatetime(code.expiresAt) : null;
    if (newExpires !== oldExpires) {
      body.expiresAt = newExpires ? dayjs(newExpires).toISOString() : null;
    }

    if (isSharedCode && data.maxRedemptions !== '') {
      const newMax = Number(data.maxRedemptions);
      if (newMax !== code.maxRedemptions) {
        body.maxRedemptions = newMax;
      }
    }

    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }

    try {
      await updateMutation.mutateAsync({ campaignId, codeId: code.id, body });
      onClose();
    } catch {
      // Error handled in mutation hook
    }
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={onSubmit}>
        <DialogTitle>Edit Code</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {codeFieldDisabled && (
              <Alert severity="info">
                {isSharedCode
                  ? 'Code value cannot be changed because it has redemptions.'
                  : 'Code value cannot be changed because it has been claimed.'}
              </Alert>
            )}

            <Controller
              name="code"
              control={control}
              rules={{ required: 'Code is required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Code"
                  required
                  fullWidth
                  disabled={codeFieldDisabled}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name="expiresAt"
              control={control}
              rules={{
                validate: (val) => {
                  if (!val) return true;
                  return dayjs(val).isAfter(dayjs()) || 'Expiry must be in the future';
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Expires At"
                  type="datetime-local"
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={!!error}
                  helperText={error?.message ?? 'Leave empty for no expiry'}
                />
              )}
            />

            {isSharedCode && (
              <Controller
                name="maxRedemptions"
                control={control}
                rules={{
                  validate: (val) => {
                    if (!val) return true;
                    const num = Number(val);
                    if (!Number.isInteger(num) || num < 1) return 'Must be a positive integer';
                    if (code && num < code.currentRedemptions) {
                      return `Cannot be less than current redemptions (${code.currentRedemptions})`;
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Max Redemptions"
                    type="number"
                    fullWidth
                    slotProps={{ htmlInput: { min: 1 } }}
                    error={!!error}
                    helperText={error?.message ?? (code ? `Current redemptions: ${code.currentRedemptions}` : '')}
                  />
                )}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
